import * as t from 'io-ts';

import {
  makeEnumRuntimeType,
  RApiTokenIdRef,
  RLocalApplicationIdRef,
  RTagIdRef,
} from '../../../models/api/apiTypes';
import {RAsyncStatus} from '../../appTypes';
import {PanelTypesEnum, RPanelStateBase, RSettingsPanelId} from '../panelStateBase';

/*
 * Runtime types.
 */

export enum SettingsPanelResourceTypesEnum {
  NONE = 'NONE',
  TAGS = 'TAGS',
  RULES = 'RULES',
  TEAMMATES = 'TEAMMATES',
  CUSTOM_FIELDS = 'CUSTOM_FIELDS',
  SIGNATURES = 'SIGNATURES',
  DEVELOPERS = 'DEVELOPERS',
}

const RSettingsPanelBaseResourcesState = t.type({
  resourceType: makeEnumRuntimeType(SettingsPanelResourceTypesEnum),
});

const RSettingsPanelNoResourceState = t.intersection([
  RSettingsPanelBaseResourcesState,
  t.type({
    resourceType: t.literal(SettingsPanelResourceTypesEnum.NONE),
  }),
]);

const RSettingsPanelTagsState = t.intersection([
  RSettingsPanelBaseResourcesState,
  t.type({
    resourceType: t.literal(SettingsPanelResourceTypesEnum.TAGS),
    referenceResourceIds: t.readonlyArray(RTagIdRef),
  }),
]);

const RSettingsPanelRulesState = t.intersection([
  RSettingsPanelBaseResourcesState,
  t.type({
    resourceType: t.literal(SettingsPanelResourceTypesEnum.RULES),
  }),
]);

const RSettingsPanelTeammatesState = t.intersection([
  RSettingsPanelBaseResourcesState,
  t.type({
    resourceType: t.literal(SettingsPanelResourceTypesEnum.TEAMMATES),
  }),
]);

const RSettingsPanelCustomFieldsState = t.intersection([
  RSettingsPanelBaseResourcesState,
  t.type({
    resourceType: t.literal(SettingsPanelResourceTypesEnum.CUSTOM_FIELDS),
  }),
  t.partial({
    customFieldIds: t.readonlyArray(t.number),
    selectedCustomFieldIds: t.readonlyArray(t.number),
    status: RAsyncStatus,
  }),
]);

const RSettingsPanelSignaturesState = t.intersection([
  RSettingsPanelBaseResourcesState,
  t.type({
    resourceType: t.literal(SettingsPanelResourceTypesEnum.SIGNATURES),
    signatureIds: t.readonlyArray(t.number),
  }),
  t.partial({
    status: RAsyncStatus,
  }),
]);

const RSettingsPanelDevelopersState = t.intersection([
  RSettingsPanelBaseResourcesState,
  t.type({
    resourceType: t.literal(SettingsPanelResourceTypesEnum.DEVELOPERS),
  }),
  t.partial({
    // Single Application.
    localApplicationId: RLocalApplicationIdRef,
    localApplicationAsyncStatus: RAsyncStatus,
    // List of applications (table).
    localApplicationIds: t.readonlyArray(RLocalApplicationIdRef),
    localApplicationsAsyncStatus: RAsyncStatus,
    canLoadMoreLocalApplications: t.boolean,
    // List of api tokens (table).
    apiTokenIds: t.readonlyArray(RApiTokenIdRef),
    apiTokenAsyncStatus: RAsyncStatus,
  }),
]);

const RSettingsPanelResourcesState = t.taggedUnion('resourceType', [
  RSettingsPanelNoResourceState,
  RSettingsPanelTagsState,
  RSettingsPanelRulesState,
  RSettingsPanelTeammatesState,
  RSettingsPanelCustomFieldsState,
  RSettingsPanelSignaturesState,
  RSettingsPanelDevelopersState,
]);

export const RSettingsPanelStateDirty = t.intersection([
  RPanelStateBase,
  t.type({
    id: RSettingsPanelId,
    type: t.literal(PanelTypesEnum.SETTINGS),
    resources: RSettingsPanelResourcesState,
  }),
]);

/*
 * Types.
 */

export interface SettingsPanelState extends t.TypeOf<typeof RSettingsPanelStateDirty> {}
export const RSettingsPanelState = t.clean<SettingsPanelState>(RSettingsPanelStateDirty as any);

export interface SettingsPanelBaseResourcesState extends t.TypeOf<typeof RSettingsPanelBaseResourcesState> {}
export interface SettingsPanelCustomFieldsResourcesState
  extends t.TypeOf<typeof RSettingsPanelCustomFieldsState> {}
export interface SettingsPanelTagsResourcesState extends t.TypeOf<typeof RSettingsPanelTagsState> {}
export interface SettingsPanelSignaturesResourceState
  extends t.TypeOf<typeof RSettingsPanelSignaturesState> {}
export interface SettingsPanelDevelopersState extends t.TypeOf<typeof RSettingsPanelDevelopersState> {}

/*
 * Type Guards.
 */

export function isSettingsResourcesTagsResource(
  state: SettingsPanelBaseResourcesState,
): state is SettingsPanelTagsResourcesState {
  return state.resourceType === SettingsPanelResourceTypesEnum.TAGS;
}

export function isSettingsResourcesCustomFieldsResource(
  state: SettingsPanelBaseResourcesState,
): state is SettingsPanelCustomFieldsResourcesState {
  return state.resourceType === SettingsPanelResourceTypesEnum.CUSTOM_FIELDS;
}

export function isSettingsResourcesSignaturesResource(
  state: SettingsPanelBaseResourcesState,
): state is SettingsPanelSignaturesResourceState {
  return state.resourceType === SettingsPanelResourceTypesEnum.SIGNATURES;
}

export function isSettingsResourcesDevelopersResource(
  state?: SettingsPanelBaseResourcesState,
): state is SettingsPanelDevelopersState {
  return Boolean(state && state.resourceType === SettingsPanelResourceTypesEnum.DEVELOPERS);
}
